import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

import { useAppDispatch, useAppSelector } from "store/index";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import { usePermissions } from "helpers/usePermission";

import RoundBtn from "components/buttons/RoundBtn";

import MainPageDragItem from "./components/MainPageDragItem";
import MainPageSortItem from "./components/MainPageSortItem";

import icon from "assets/icons/burger-icon.svg";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import "./Widgets.scss";
import WidgetsListSkeleton from "components/global/Skeleton/WidgetsListSkeleton/WidgetsListSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

interface IPageItem {
  instanceType: string;
  priorityOrder: number;
}

const Header: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activePermissions = usePermissions("cms_widgets_header");

  const geoList = useAppSelector(selectFilteredGeo);

  const [activeGeo, setActiveGeo] = useState<string>();
  const [headerItems, setHeaderItems] = useState<
    { value: string; label: string; img: string }[]
  >([]);
  const [pageItems, setPageItems] = useState<
    { value: string; label: string }[]
  >([]);
  const [itemsFetched, setItemsFetched] = useState(false);
  const [selectedItems, setSelectedItems] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState<
    { value: string; label: string }[]
    >([]);
  const permissions = usePermissionHelper();

  useEffect(() => {
    if (!activePermissions?.includes("read") && activePermissions !== null) {
      navigate("/dashboard");
    }
  }, []);

  useEffect(() => {
    if (geoList?.length) {
      setActiveGeo(geoList[0]);
    }
  }, [geoList]);

  const getPageItems = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.headerList}?geo=${activeGeo}`,
      );
      setPageItems(
        result.data.items.map((el: IPageItem) => ({
          value: el.instanceType,
          type: el.instanceType,
          label: t(el.instanceType),
          img: icon,
        })),
      );
    } catch (error) {
      console.error(error);
    }
    setItemsFetched(true);
  };

  useEffect(() => {
    if (activeGeo) {
      setItemsFetched(false);
      getPageItems();
    }
  }, [activeGeo]);

  const getItems = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.headerItems}?limit=500&offset=0`,
      );
      setHeaderItems(
        result.data.items.map((el: string) => ({
          value: el,
          label: t(el),
          img: icon,
          type: "burger",
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getItems();
  }, []);

  const handleUpdateItems = async (items: any[]) => {
    try {
      await instance.post(`${apiRoutes.headerUpdate}`, {
        geo: activeGeo,
        items: items.map((el, idx) => {
          return {
            instanceType: el.value,
            priorityOrder: idx + 1,
          };
        }),
      });
      setPageItems([]);
      setItemsFetched(false);
      getPageItems();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const moveItem = async (dragIndex: number, hoverIndex: number) => {
    let items = Array.from(pageItems);
    const [reorderedItem] = items.splice(dragIndex, 1);
    items.splice(hoverIndex, 0, reorderedItem);
    setPageItems(items);
    setItemsFetched(false);
    try {
      await instance.post(`${apiRoutes.headerUpdate}`, {
        geo: activeGeo,
        items: items.map((el, idx) => {
          return {
            instanceType: el.value,
            priorityOrder: idx + 1,
          };
        }),
      });
      setPageItems([]);
      setItemsFetched(false);
      getPageItems();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsOrderUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsOrderUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
    setItemsFetched(true);
  };

  

  return (
    <div className="main-page-wrapper burger-page-wrapper">
      <div className="main-page-content">
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-widget--tabs">
              <div className="game-modal-tabs small">
                {geoList.map((el) => (
                  <div
                    className={`tab-item ${activeGeo === el ? "active" : ""}`}
                    key={el}
                    onClick={() => {
                      setActiveGeo(el);
                      setPageItems([]);
                    }}
                  >
                    {el.toLowerCase() === "ww" ? (
                      <span className="mr-5">🌎</span>
                    ) : (
                      <img
                        src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                        alt={el}
                      />
                    )}
                    {t(el)}
                  </div>
                ))}
              </div>
            </div>
            {itemsFetched ? (
              <div className="main-page-widget--list">
                {pageItems.map((el, index) => (
                  <MainPageSortItem
                    isBurger
                    handleClick={() => {
                      if (selectedItemsToDelete.includes(el)) {
                        setSelectedItemsToDelete(
                          selectedItemsToDelete.filter((item) => item !== el),
                        );
                      } else {
                        setSelectedItemsToDelete([
                          ...selectedItemsToDelete,
                          el,
                        ]);
                      }
                    }}
                    isSelected={selectedItemsToDelete.includes(el)}
                    key={`${el.value}`}
                    item={el}
                    index={index}
                    moveItem={moveItem}
                    isDraggable={permissions?.update}
                    readOnly={!permissions?.update}
                  />
                ))}
              </div>
            ) : (
              <WidgetsListSkeleton />
            )}
            <div className="main-page-btns">
              {permissions?.create && (
                <RoundBtn
                  handleClick={() => {
                    setItemsFetched(false);
                    const newArray = [...pageItems, ...selectedItems];
                    handleUpdateItems(newArray);
                    setSelectedItems([]);
                    setPageItems(newArray);
                  }}
                  txt="+"
                  disabled={selectedItems.length === 0}
                />
              )}
              {permissions?.delete && (
                <RoundBtn
                  handleClick={() => {
                    setItemsFetched(false);
                    const newArray = pageItems.filter(
                      (el) => !selectedItemsToDelete.includes(el),
                    );
                    handleUpdateItems(newArray);
                    setSelectedItemsToDelete([]);
                    setPageItems(newArray);
                  }}
                  txt="-"
                  disabled={selectedItemsToDelete.length === 0}
                />
              )}
            </div>
          </div>
        </div>
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-title">{t("menuItems")}</div>
            <div className="main-page-list-content">
              {itemsFetched ? (
                <>
                  {headerItems
                    .filter(
                      (item) =>
                        !pageItems.map((p) => p.value).includes(item.value),
                    )
                    .map((el: any) => (
                      <MainPageDragItem
                        isBurger
                        handleClick={() => {
                          if (selectedItems.includes(el)) {
                            setSelectedItems(
                              selectedItems.filter((item) => item !== el),
                            );
                          } else {
                            setSelectedItems([...selectedItems, el]);
                          }
                        }}
                        isSelected={selectedItems.includes(el)}
                        key={el.value}
                        item={el}
                        type="header"
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
